import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AlertTriangle, Loader2, X } from "lucide-react";

import api from "../../services/api";
import { formatMoney } from "../../helpers/money";
import useBillingStatus from "../../hooks/useBillingStatus";

/**
 * Shown to everyone in the installation once an invoice is close to (or past)
 * its due date. Deliberately not dismissible forever — it reappears on reload,
 * because the alternative is a customer being suspended with no warning.
 */
const BillingBanner = () => {
  const history = useHistory();
  const isMounted = useRef(true);
  const { status } = useBillingStatus();
  const [paying, setPaying] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => () => { isMounted.current = false; }, []);

  const invoice = status?.pendingInvoice;

  if (hidden || !status?.enabled || !invoice) return null;

  // Stay quiet until the due date is within a week.
  const days = invoice.daysUntilDue;
  if (days !== null && days > 7) return null;

  const isOverdue = invoice.status === "overdue" || (days !== null && days < 0);

  const handlePay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/billing/invoices/${invoice.id}/checkout`);
      window.location.href = data.url;
    } catch {
      if (isMounted.current) setPaying(false);
      history.push("/billing");
    }
  };

  const message = isOverdue
    ? `Factura ${invoice.number} vencida. El acceso se suspenderá ${
        status.graceDays ? `a los ${status.graceDays} días de mora` : "en breve"
      }.`
    : `Factura ${invoice.number} vence ${
        days === 0 ? "hoy" : `en ${days} día${days === 1 ? "" : "s"}`
      }.`;

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 text-sm border-b ${
        isOverdue
          ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-700 dark:text-red-300"
          : "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300"
      }`}
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <p className="flex-1 min-w-0">
        {message}{" "}
        <span className="font-semibold">
          {formatMoney(invoice.totalCents, invoice.currency)}
        </span>
      </p>
      <button
        onClick={handlePay}
        disabled={paying}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-white transition-colors disabled:opacity-50 ${
          isOverdue
            ? "bg-red-600 hover:bg-red-700"
            : "bg-amber-600 hover:bg-amber-700"
        }`}
      >
        {paying && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Pagar ahora
      </button>
      <button
        onClick={() => setHidden(true)}
        title="Ocultar hasta recargar"
        className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default BillingBanner;
